import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { useEffect } from 'react';

// Hero Section with CTA
const ServiceHero = () => (
  <section className="relative min-h-[500px] flex items-center" data-testid="service-hero">
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_cd4f5c4d-92f8-4312-8a77-df80f7c24853/artifacts/r7pg4pr4_iStock-2155879454.jpg')`
      }}
    />
    <div className="absolute inset-0 bg-[#1a2e44]/80" />
    <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-20">
      <h1 className="font-bold text-white mb-6 uppercase leading-tight">
        <span className="block text-3xl md:text-4xl lg:text-5xl">Refined Landscape Maintenance,</span>
        <span className="block text-xl md:text-2xl lg:text-3xl mt-2 font-medium">Professionally Managed</span>
      </h1>
      <p className="text-xl text-slate-300 max-w-2xl mb-8">
        Maintaining the appearance, health, and long-term value of your property through structured maintenance programs.
      </p>
      <Link
        to="/contact?intent=assessment&service=landscape&source=service-landscape"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="hero-cta-btn"
      >
        Request a Site Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

// What's Included Section
const IncludesSection = () => {
  const services = [
    'Lawn care and turf management',
    'Hedge and shrub trimming',
    'Tree care and pruning',
    'Seasonal planting and flower bed maintenance',
    'Weed control and fertilization programs',
    'Mulching and ground cover maintenance'
  ];

  return (
    <section className="bg-white py-20" data-testid="includes-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-[#1a2e44] mb-4">
          What Our Maintenance Programs Include
        </h2>
        <p className="text-slate-600 mb-10 max-w-2xl">
          Our landscape maintenance programs are customized to each property's specific requirements:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((service, index) => (
            <div key={index} className="flex items-start gap-3 p-4 bg-slate-50">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">{service}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why It Matters Section
const WhyItMattersSection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="why-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-white mb-6">
        Why Landscape Maintenance Matters
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Preserve Property Value</h3>
          <p className="text-slate-300 text-sm">
            Consistent landscape maintenance preserves and enhances your property's market value and curb appeal.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Maintain Estate Standards</h3>
          <p className="text-slate-300 text-sm">
            Regular care ensures your property meets appearance standards and reflects the quality you expect.
          </p>
        </div>
        <div className="bg-white/10 p-6">
          <h3 className="text-lg font-bold text-white mb-3">Prevent Long-Term Landscape Decline</h3>
          <p className="text-slate-300 text-sm">
            Proactive maintenance prevents small problems from becoming expensive repairs or replacements.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// How We Deliver Section
const DeliverySection = () => (
  <section className="bg-slate-50 py-20" data-testid="delivery-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <h2 className="text-3xl font-bold text-[#1a2e44] mb-10">
        Our Structured Service Process
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">01</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Property Assessment</h3>
          <p className="text-slate-600 text-sm">
            We evaluate your landscape and understand your priorities.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">02</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Customized Program</h3>
          <p className="text-slate-600 text-sm">
            We develop a maintenance program tailored to your property.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">03</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Scheduled Service</h3>
          <p className="text-slate-600 text-sm">
            Our teams execute the program on a consistent schedule.
          </p>
        </div>
        <div className="bg-white p-6 border-t-4 border-t-[#1a2e44]">
          <div className="text-4xl font-bold text-[#1a2e44] mb-3">04</div>
          <h3 className="text-lg font-bold text-[#1a2e44] mb-2">Documentation</h3>
          <p className="text-slate-600 text-sm">
            Every visit is documented with reports and photos.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Documentation Section
const DocumentationSection = () => (
  <section className="bg-white py-20" data-testid="documentation-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#1a2e44] mb-6">
            Documentation & Reporting
          </h2>
          <p className="text-slate-600 mb-8">
            Every service visit includes detailed documentation so you always know what's happening on your property:
          </p>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Before-and-after photos</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Service completion confirmations</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Property condition observations</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#1a2e44] mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Recommendations for improvements</span>
            </li>
          </ul>
        </div>
        <div className="bg-slate-50 p-8 border border-slate-200">
          <p className="text-slate-600 italic text-lg">
            "Our documentation keeps you informed about your property's condition—without needing to be on-site."
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Service Area Section
const ServiceAreaSection = () => (
  <section className="bg-slate-50 py-16" data-testid="service-area-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-2xl font-bold text-[#1a2e44] mb-4">
        Service Area
      </h2>
      <p className="text-slate-600 max-w-2xl mx-auto">
        Serving Pasadena, Arcadia, San Marino, La Cañada Flintridge, and communities throughout Los Angeles County.
      </p>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="bg-[#1a2e44] py-20" data-testid="cta-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-bold text-white mb-4">
        Schedule a Property Assessment
      </h2>
      <p className="text-slate-400 mb-8 max-w-xl mx-auto">
        The first step is a site assessment where we evaluate your landscape and develop a customized maintenance program.
      </p>
      <Link
        to="/contact?intent=assessment&service=landscape&source=service-landscape"
        className="inline-flex items-center justify-center bg-[#1a2e44] hover:bg-[#0f1d2d] text-white text-sm font-bold py-4 px-8 uppercase tracking-wide transition-colors border border-white/20"
        data-testid="cta-btn"
      >
        Request a Site Assessment
        <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  </section>
);

export default function LandscapeMaintenancePage() {
  useEffect(() => {
    document.title = 'Landscape Maintenance | Sunlight Grounds Care';
  }, []);

  return (
    <div data-testid="landscape-maintenance-page">
      <ServiceHero />
      <IncludesSection />
      <WhyItMattersSection />
      <DeliverySection />
      <DocumentationSection />
      <CTASection />
      <ServiceAreaSection />
    </div>
  );
}
